import { METRICTYPE } from 'src/utils/entityDefinitions';
import { SplineChartV2Series, SplineChartV2DataPoint } from 'src/components/BeaconV2Components/SplineChartV2/SplineChartV2';
import { timestampToWeekId } from 'src/utils/dateUtils';

/**
 * @deprecated Use SegmentChartDataV2 instead for SplineChartV2 compatibility
 */
export interface SegmentChartData {
  primaryData: number[][];
  secondaryData: number[][];
}

/**
 * Chart data formatted for SplineChartV2
 */
export interface SegmentChartDataV2 {
  primarySeries: SplineChartV2Series;
  comparisonSeries: SplineChartV2Series | undefined;
}

export interface SegmentLegendData {
  formattedPrimaryTotal: string;
  percentChange: number;
  metricType: METRICTYPE;
}

/**
 * @deprecated Use TransformedSegmentDataV2 instead for SplineChartV2 compatibility
 */
export interface TransformedSegmentData {
  chartData: SegmentChartData;
  legendData: SegmentLegendData;
}

/**
 * Transformed data formatted for SplineChartV2
 */
export interface TransformedSegmentDataV2 {
  chartData: SegmentChartDataV2;
  legendData: SegmentLegendData;
}

/**
 * Converts timestamp-based data to SplineChartV2DataPoint array
 */
const toSplineChartV2DataPoints = (data: number[][]): SplineChartV2DataPoint[] => {
  if (!data || data.length === 0) return [];
  
  return data
    .map((point) => ({
      weekId: timestampToWeekId(point[0]),
      value: point[1]
    }))
    .sort((a, b) => a.weekId - b.weekId);
};

export class AtlasSegmentDataAdapter {
  private rawData: any;
  private fieldName: string;
  private formatter: (value: number, metricType: METRICTYPE, options?: any) => string;
  private comparisonTimePeriod: any;

  constructor(
    rawData: any,
    fieldName: string,
    formatter: (value: number, metricType: METRICTYPE, options?: any) => string,
    comparisonTimePeriod: any
  ) {
    this.rawData = rawData;
    this.fieldName = fieldName;
    this.formatter = formatter;
    this.comparisonTimePeriod = comparisonTimePeriod;
  }

  /**
   * @deprecated Use transformV2() instead for SplineChartV2 compatibility
   */
  transform(): TransformedSegmentData {
    const {
      primaryData: hookPrimaryData,
      secondaryData: hookSecondaryData,
      primaryTotal,
      percentChange: hookPercentChange
    } = this.rawData;

    const mainData = hookPrimaryData || [];
    let compData = hookSecondaryData || [];

    if (
      this.comparisonTimePeriod.id === 'prior-year' &&
      mainData.length > 0 &&
      compData.length > 0 &&
      mainData.length === compData.length &&
      mainData[0][0] !== compData[0][0]
    ) {
      compData = compData.map((dataPoint: number[], index: number) => {
        if (mainData[index]) {
          return [mainData[index][0], dataPoint[1]];
        }
        return dataPoint;
      });
    }

    const formattedPrimaryTotal = this.formatter(
      primaryTotal,
      this.fieldName === 'retailSales' ? METRICTYPE.MONEY : METRICTYPE.VOLUME,
      { decimalPlaces: 2, showFullValue: false }
    );

    return {
      chartData: {
        primaryData: mainData,
        secondaryData: compData
      },
      legendData: {
        formattedPrimaryTotal,
        percentChange: hookPercentChange,
        metricType: this.fieldName === 'retailSales' ? METRICTYPE.MONEY : METRICTYPE.VOLUME
      }
    };
  }

  /**
   * Transform data to SplineChartV2 format
   * Returns weekId-based series data that works correctly with SplineChartV2
   */
  transformV2(): TransformedSegmentDataV2 {
    const {
      primaryData: hookPrimaryData,
      secondaryData: hookSecondaryData,
      primaryTotal,
      percentChange: hookPercentChange
    } = this.rawData;

    const mainData = hookPrimaryData || [];
    const compData = hookSecondaryData || [];

    // Convert to weekId-based data - no year shifting needed
    // SplineChartV2 handles alignment via categories
    const primaryDataPoints = toSplineChartV2DataPoints(mainData);
    const comparisonDataPoints = toSplineChartV2DataPoints(compData);

    const primarySeries: SplineChartV2Series = {
      id: 'primary',
      name: 'Primary',
      data: primaryDataPoints
    };

    const comparisonSeries: SplineChartV2Series | undefined = comparisonDataPoints.length > 0
      ? {
          id: 'comparison',
          name: 'Comparison',
          data: comparisonDataPoints
        }
      : undefined;

    const formattedPrimaryTotal = this.formatter(
      primaryTotal,
      this.fieldName === 'retailSales' ? METRICTYPE.MONEY : METRICTYPE.VOLUME,
      { decimalPlaces: 2, showFullValue: false }
    );

    return {
      chartData: {
        primarySeries,
        comparisonSeries
      },
      legendData: {
        formattedPrimaryTotal,
        percentChange: hookPercentChange,
        metricType: this.fieldName === 'retailSales' ? METRICTYPE.MONEY : METRICTYPE.VOLUME
      }
    };
  }

  hasValidData(): boolean {
    return !!(this.rawData.response?.primary && !this.rawData.isLoading);
  }

  isLoading(): boolean {
    return this.rawData.isLoading;
  }
}
